/**
 * Course Viewer - LMS
 * Learner view for taking courses
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ModuleBlockViewer from './ModuleBlockViewer';
import toast from '../utils/toast';

function CourseViewer() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [course, setCourse] = useState(null);
  const [modules, setModules] = useState([]);
  const [currentModuleIndex, setCurrentModuleIndex] = useState(0);
  const [completedModules, setCompletedModules] = useState([]);
  const [progressPercentage, setProgressPercentage] = useState(0);
  const [isCompleting, setIsCompleting] = useState(false);
  const [completionStatus, setCompletionStatus] = useState(null);
  const [certificateStatus, setCertificateStatus] = useState(null);
  const [previousCertificateAvailable, setPreviousCertificateAvailable] = useState(false);

  const userId = JSON.parse(localStorage.getItem('user') || '{}').id;

  // Inject animation CSS (only once)
  useEffect(() => {
    const styleId = 'course-viewer-animations';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        @keyframes badgeFadeIn {
          from {
            opacity: 0;
            transform: scale(0.5);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }
        @keyframes certificateBounce {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        @keyframes progressPulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.9;
          }
        }
        .badge-animate {
          animation: badgeFadeIn 0.5s ease-out forwards;
        }
        .certificate-unlock {
          animation: certificateBounce 0.6s ease-out;
        }
        .progress-animate {
          animation: progressPulse 2s ease-in-out infinite;
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  useEffect(() => {
    fetchCourseAndProgress();
  }, [courseId]);

  // Fetch completion status when current module changes
  useEffect(() => {
    if (currentModule && currentModule.id) {
      fetchCompletionStatus();
    }
  }, [currentModuleIndex, currentModule?.id]);

  // Poll completion status every 5 seconds while viewing a module
  useEffect(() => {
    if (!currentModule || !currentModule.id || isCurrentModuleCompleted) return;

    const interval = setInterval(() => {
      fetchCompletionStatus();
    }, 5000);

    return () => clearInterval(interval);
  }, [currentModule?.id, isCurrentModuleCompleted]);

  // Fetch certificate status when course is loaded
  useEffect(() => {
    if (courseId) {
      fetchCertificateStatus();
    }
  }, [courseId, completedModules]);

  // Poll certificate status every 5 seconds until unlocked
  useEffect(() => {
    if (!courseId || (certificateStatus && certificateStatus.certificate_available)) return;

    const interval = setInterval(() => {
      fetchCertificateStatus();
    }, 5000);

    return () => clearInterval(interval);
  }, [courseId, certificateStatus?.certificate_available]);

  // Show toast when certificate unlocks
  useEffect(() => {
    if (certificateStatus && certificateStatus.certificate_available && !previousCertificateAvailable) {
      toast.success('Congratulations! Your certificate is now available for download!');
      setPreviousCertificateAvailable(true);
    }
  }, [certificateStatus?.certificate_available]);

  const fetchCourseAndProgress = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');

      // Fetch course details
      const courseResponse = await fetch(`/courses/${courseId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!courseResponse.ok) throw new Error('Failed to fetch course');
      const courseData = await courseResponse.json();

      setCourse(courseData);
      setModules(courseData.modules || []);

      // Fetch progress
      const progressResponse = await fetch(`/users/${userId}/course-progress`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (progressResponse.ok) {
        const progressData = await progressResponse.json();
        const courseProgress = progressData.find(p => p.course_id === parseInt(courseId));

        if (courseProgress) {
          setCompletedModules(courseProgress.completed_module_ids || []);
          setProgressPercentage(courseProgress.progress_percentage || 0);
        }
      }

      setError(null);
    } catch (err) {
      console.error('Error fetching course:', err);
      setError('Failed to load course');
    } finally {
      setLoading(false);
    }
  };

  const fetchCompletionStatus = async () => {
    const currentModule = modules[currentModuleIndex];
    if (!currentModule) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/modules/${currentModule.id}/completion-status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCompletionStatus(data);

        // If module is now complete, refresh course progress
        if (data.is_complete && !completedModules.includes(currentModule.id)) {
          await handleMarkComplete();
        }
      }
    } catch (err) {
      console.error('Error fetching completion status:', err);
    }
  };

  const fetchCertificateStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/courses/${courseId}/certificate-status`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setCertificateStatus(data);
      }
    } catch (err) {
      console.error('Error fetching certificate status:', err);
    }
  };

  const handleMarkComplete = async () => {
    const currentModule = modules[currentModuleIndex];
    if (!currentModule) return;

    setIsCompleting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/courses/${courseId}/modules/${currentModule.id}/complete`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to mark module complete');

      // Refresh progress
      await fetchCourseAndProgress();

      // Move to next module if available
      if (currentModuleIndex < modules.length - 1) {
        setCurrentModuleIndex(currentModuleIndex + 1);
      } else {
        alert('Congratulations! You have completed the course. Your certificate will be generated.');
      }
    } catch (err) {
      console.error('Error marking module complete:', err);
      alert('Failed to mark module complete');
    } finally {
      setIsCompleting(false);
    }
  };

  const handleModuleClick = (index) => {
    setCurrentModuleIndex(index);
  };

  const handlePrevious = () => {
    if (currentModuleIndex > 0) {
      setCurrentModuleIndex(currentModuleIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentModuleIndex < modules.length - 1) {
      setCurrentModuleIndex(currentModuleIndex + 1);
    }
  };

  const containerStyle = {
    padding: '24px',
    maxWidth: '1400px',
    margin: '0 auto'
  };

  const headerStyle = {
    marginBottom: '24px'
  };

  const titleStyle = {
    fontSize: '2rem',
    fontWeight: '700',
    color: '#1a1a1a',
    margin: 0
  };

  const progressBarContainerStyle = {
    background: '#f0f0f0',
    borderRadius: '20px',
    height: '32px',
    marginBottom: '24px',
    overflow: 'hidden',
    position: 'relative',
    border: '2px solid #d4af37'
  };

  const progressBarStyle = {
    background: 'linear-gradient(90deg, #d4af37 0%, #f4d03f 100%)',
    height: '100%',
    width: `${progressPercentage}%`,
    transition: 'width 0.3s ease'
  };

  const progressTextStyle = {
    position: 'absolute',
    width: '100%',
    textAlign: 'center',
    lineHeight: '32px',
    fontSize: '0.95rem',
    fontWeight: '700',
    color: '#1a1a1a'
  };

  const layoutStyle = {
    display: 'grid',
    gridTemplateColumns: '300px 1fr',
    gap: '24px'
  };

  const sidebarStyle = {
    background: '#fff',
    border: '2px solid #d4af37',
    borderRadius: '8px',
    padding: '20px',
    height: 'fit-content',
    position: 'sticky',
    top: '20px'
  };

  const moduleListItemStyle = (index, isCompleted, isCurrent) => ({
    padding: '12px',
    marginBottom: '8px',
    background: isCurrent ? '#d4af37' : '#f9f9f9',
    border: isCurrent ? '2px solid #d4af37' : '1px solid #ddd',
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '0.9rem',
    fontWeight: isCurrent ? '700' : '500',
    color: isCurrent ? '#1a1a1a' : '#333'
  });

  const contentAreaStyle = {
    background: '#fff',
    border: '2px solid #d4af37',
    borderRadius: '8px',
    padding: '32px'
  };

  const moduleContentStyle = {
    fontSize: '1rem',
    lineHeight: '1.8',
    color: '#333',
    whiteSpace: 'pre-wrap',
    marginBottom: '32px',
    minHeight: '300px'
  };

  const buttonStyle = {
    padding: '12px 24px',
    fontSize: '1rem',
    fontWeight: '600',
    background: '#d4af37',
    color: '#1a1a1a',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    marginRight: '8px'
  };

  const secondaryButtonStyle = {
    ...buttonStyle,
    background: '#fff',
    border: '2px solid #d4af37'
  };

  const navigationStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '24px',
    paddingTop: '24px',
    borderTop: '2px solid #f0f0f0'
  };

  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={{ padding: '48px', textAlign: 'center', background: '#fff', border: '2px solid #d4af37', borderRadius: '8px' }}>
          Loading course...
        </div>
      </div>
    );
  }

  if (error || !course) {
    return (
      <div style={containerStyle}>
        <div style={{ padding: '48px', textAlign: 'center', background: '#ffebee', border: '2px solid #e74c3c', borderRadius: '8px', color: '#c62828' }}>
          {error || 'Course not found'}
        </div>
      </div>
    );
  }

  const currentModule = modules[currentModuleIndex];
  const isCurrentModuleCompleted = currentModule ? completedModules.includes(currentModule.id) : false;

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <h1 style={titleStyle}>{course.title}</h1>
          <button onClick={() => navigate('/my-courses')} style={secondaryButtonStyle}>
            Back to My Courses
          </button>
        </div>
        {course.description && (
          <p style={{ color: '#666', fontSize: '1rem', margin: '8px 0 16px 0' }}>
            {course.description}
          </p>
        )}

        <div style={progressBarContainerStyle}>
          <div
            style={progressBarStyle}
            className={progressPercentage > 0 && progressPercentage < 100 ? 'progress-animate' : ''}
          ></div>
          <div style={progressTextStyle}>
            {progressPercentage.toFixed(0)}% Complete - {completedModules.length} of {modules.length} modules
          </div>
        </div>
      </div>

      <div style={layoutStyle}>
        {/* Sidebar - Module List */}
        <div style={sidebarStyle}>
          <h3 style={{ fontSize: '1.2rem', fontWeight: '700', marginBottom: '16px', color: '#1a1a1a' }}>
            Course Modules
          </h3>
          {modules.map((module, index) => {
            const isCompleted = completedModules.includes(module.id);
            const isCurrent = index === currentModuleIndex;

            return (
              <div
                key={module.id}
                onClick={() => handleModuleClick(index)}
                style={moduleListItemStyle(index, isCompleted, isCurrent)}
              >
                <span style={{ fontSize: '1.2rem' }}>
                  {isCompleted ? 'Done' : index + 1}
                </span>
                <span style={{ flex: 1 }}>
                  {module.title}
                </span>
              </div>
            );
          })}

          {/* Certificate Status Box */}
          {certificateStatus && (
            <div
              className={certificateStatus.certificate_available ? 'certificate-unlock' : ''}
              style={{
                marginTop: '24px',
                padding: '20px',
                background: certificateStatus.certificate_available ? '#e8f5e9' : '#fff9e6',
                border: `2px solid ${certificateStatus.certificate_available ? '#4caf50' : '#d4af37'}`,
                borderRadius: '8px'
              }}
            >
              <h3 style={{
                margin: '0 0 12px 0',
                fontSize: '1rem',
                fontWeight: '700',
                color: certificateStatus.certificate_available ? '#2e7d32' : '#1a1a1a'
              }}>
                {certificateStatus.certificate_available ? 'Certificate Ready' : 'Certificate Locked'}
              </h3>

              {certificateStatus.certificate_available ? (
                <div>
                  <p style={{ margin: '0 0 16px 0', fontSize: '0.9rem', color: '#2e7d32' }}>
                    Congratulations! You've met all requirements.
                  </p>
                  <button
                    onClick={() => alert('Certificate download feature coming soon!')}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      background: '#4caf50',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontWeight: '700',
                      fontSize: '0.95rem'
                    }}
                  >
                    Download Certificate
                  </button>
                </div>
              ) : (
                <div style={{ fontSize: '0.85rem', lineHeight: '1.6', color: '#666' }}>
                  <p style={{ margin: '0 0 12px 0', fontStyle: 'italic' }}>
                    Your certificate will unlock automatically once all course requirements are met.
                  </p>
                  <div style={{ fontSize: '0.8rem' }}>
                    <div style={{ marginBottom: '4px' }}>
                      {certificateStatus.modules_completed}/{certificateStatus.modules_required} modules completed
                    </div>
                    {certificateStatus.reason_locked && (
                      <div style={{ marginTop: '8px', padding: '8px', background: '#fff', borderRadius: '4px', border: '1px solid #ddd' }}>
                        {certificateStatus.reason_locked}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Main Content Area */}
        <div style={contentAreaStyle}>
          {currentModule ? (
            <>
              <h2 style={{ fontSize: '1.8rem', fontWeight: '700', marginBottom: '8px', color: '#1a1a1a' }}>
                Module {currentModuleIndex + 1}: {currentModule.title}
              </h2>

              {isCurrentModuleCompleted && (
                <div
                  className="badge-animate"
                  style={{
                    display: 'inline-block',
                    padding: '6px 12px',
                    borderRadius: '20px',
                    fontSize: '0.75rem',
                    fontWeight: '700',
                    textTransform: 'uppercase',
                    background: '#4caf50',
                    color: '#fff',
                    marginBottom: '16px'
                  }}
                >
                  Completed
                </div>
              )}

              <h2 style={{ color: '#d4af37', marginBottom: '16px', fontSize: '1.3rem', marginTop: '24px' }}>
                Module Content
              </h2>

              {currentModule && currentModule.id && (
                <ModuleBlockViewer moduleId={currentModule.id} />
              )}

              {/* Module Completion Requirements */}
              {completionStatus && (
                <div style={{
                  marginTop: '32px',
                  marginBottom: '24px',
                  padding: '24px',
                  background: completionStatus.is_complete ? '#e8f5e9' : '#fff9e6',
                  border: `2px solid ${completionStatus.is_complete ? '#4caf50' : '#d4af37'}`,
                  borderRadius: '8px'
                }}>
                  <h3 style={{
                    margin: '0 0 16px 0',
                    fontSize: '1.1rem',
                    fontWeight: '700',
                    color: completionStatus.is_complete ? '#2e7d32' : '#1a1a1a'
                  }}>
                    {completionStatus.is_complete ? 'Module Complete' : 'Module Requirements'}
                  </h3>

                  {!completionStatus.is_complete && (
                    <div style={{ fontSize: '0.95rem', lineHeight: '1.8', color: '#666' }}>
                      {completionStatus.required.require_quiz_pass && (
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                          <span style={{ marginRight: '12px', fontSize: '1.2rem', color: completionStatus.quiz_passed ? '#4caf50' : '#999' }}>
                            {completionStatus.quiz_passed ? 'Done' : 'TODO'}
                          </span>
                          <span>Pass the quiz</span>
                        </div>
                      )}

                      {completionStatus.required.require_assignment_submission && (
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                          <span style={{ marginRight: '12px', fontSize: '1.2rem', color: completionStatus.assignment_submitted ? '#4caf50' : '#999' }}>
                            {completionStatus.assignment_submitted ? 'Done' : 'TODO'}
                          </span>
                          <span>Submit the assignment</span>
                        </div>
                      )}

                      {completionStatus.required.require_all_blocks_viewed && (
                        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                          <span style={{ marginRight: '12px', fontSize: '1.2rem', color: completionStatus.blocks_viewed.length > 0 ? '#4caf50' : '#999' }}>
                            {completionStatus.blocks_viewed.length > 0 ? 'Done' : 'TODO'}
                          </span>
                          <span>View all content blocks</span>
                        </div>
                      )}

                      {!completionStatus.required.require_quiz_pass &&
                       !completionStatus.required.require_assignment_submission &&
                       !completionStatus.required.require_all_blocks_viewed && (
                        <div style={{ fontStyle: 'italic', color: '#999' }}>
                          No requirements set for this module. It will be marked complete automatically.
                        </div>
                      )}
                    </div>
                  )}

                  {completionStatus.is_complete && (
                    <div style={{ fontSize: '0.95rem', color: '#2e7d32', fontWeight: '600' }}>
                      All requirements met! You can now proceed to the next module.
                    </div>
                  )}
                </div>
              )}

              <div style={navigationStyle}>
                <button
                  onClick={handlePrevious}
                  disabled={currentModuleIndex === 0}
                  style={{
                    ...secondaryButtonStyle,
                    opacity: currentModuleIndex === 0 ? 0.5 : 1,
                    cursor: currentModuleIndex === 0 ? 'not-allowed' : 'pointer'
                  }}
                >
                  ← Previous Module
                </button>

                <div style={{ display: 'flex', gap: '8px' }}>
                  {currentModuleIndex < modules.length - 1 && (
                    <button onClick={handleNext} style={secondaryButtonStyle}>
                      Next Module →
                    </button>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', padding: '48px', color: '#666' }}>
              No modules available
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CourseViewer;
